import { Endpoints } from '@qcdoc/mock-runtime/contracts/endpoints.ts';
import apiClient from '../../api/common/apiClient.ts';
import type { LookupItemGuid } from '../../types/common/common.ts';

const lookupEndpoints = {
  psls: Endpoints.lookup.psls,
  productTypes: Endpoints.lookup.productTypes,
  categoryLevels: Endpoints.lookup.categoryLevels,
  qaLabs: Endpoints.lookup.qaLabs,
  vendors: Endpoints.lookup.vendors,
  rawMaterials: Endpoints.lookup.rawMaterials,
  regTypes: Endpoints.lookup.regTypes,
  specTypes: Endpoints.lookup.specTypes,
  countries: Endpoints.lookup.countries,
};

export type LookupName = keyof typeof lookupEndpoints;
export type LookupData = Record<LookupName, LookupItemGuid[]>;
export const lookupNames = Object.keys(lookupEndpoints) as LookupName[];

export interface ProductLookupOption {
  label: string;
  value: string;
}

export async function searchProductOptions(
  term: string,
  pslId?: number | string,
  signal?: AbortSignal,
): Promise<ProductLookupOption[]> {
  const { data } = await apiClient.post<{
    items: { name: string; productSAPNumber?: string }[];
  }>(Endpoints.products.list, {
    ProdName: `%${term}%`,
    PslId: pslId === undefined ? undefined : String(pslId),
    StartIndex: 0,
    MaximumRows: 15,
  }, { signal });
  return (data.items ?? []).map((product) => ({
    label: product.productSAPNumber ? `${product.name} (${product.productSAPNumber})` : product.name,
    value: product.name,
  }));
}

export interface StpLookupItem {
  id: string;
  stpNumber: string;
  stpName: string;
  description: string;
  label: string;
}

export async function getLookup(name: LookupName, signal?: AbortSignal): Promise<LookupItemGuid[]> {
  const { data } = await apiClient.get<{ oid: string; name: string }[]>(lookupEndpoints[name], { signal });
  return data.map((row) => ({ id: row.oid, name: row.name }));
}

export async function searchSTPs(term: string, signal?: AbortSignal): Promise<StpLookupItem[]> {
  const { data } = await apiClient.post<{
    oid: string; stpNumber: string; stpName: string; description: string;
  }[]>(Endpoints.stp.list, { STPNumber: term || undefined, Statuses: '14' }, { signal });
  return (data ?? []).map((row) => ({
    id: row.oid,
    stpNumber: row.stpNumber ?? '',
    stpName: row.stpName ?? '',
    description: row.description ?? '',
    label: `${row.stpNumber ?? ''}${row.description ? ' - ' + row.description : ''}`,
  }));
}